import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getInventory, createInventoryItem, updateInventoryItem, deleteInventoryItem,
  getLoans, createLoan, returnLoan, getUsers
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Plus, Package, Search, Edit, Trash2, HandMetal, RotateCcw, User } from 'lucide-react';

const CATEGORIES = [
  { value: 'trajes', label: 'Trajes' },
  { value: 'accesorios', label: 'Accesorios' },
  { value: 'instrumentos', label: 'Instrumentos' },
  { value: 'materiales', label: 'Materiales' },
  { value: 'otros', label: 'Otros' }
];

const CONDITIONS = [
  { value: 'bueno', label: 'Bueno', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'regular', label: 'Regular', color: 'bg-amber-100 text-amber-700' },
  { value: 'malo', label: 'Malo', color: 'bg-red-100 text-red-700' }
];

export default function Inventory() {
  const { hasPermission } = useAuth();
  const [items, setItems] = useState([]);
  const [loans, setLoans] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [showLoanDialog, setShowLoanDialog] = useState(null);
  const [loanUser, setLoanUser] = useState('');
  const [loanNotes, setLoanNotes] = useState('');
  const [form, setForm] = useState({
    name: '', category: 'trajes', description: '', quantity: 1, condition: 'bueno', location: ''
  });

  const canEdit = hasPermission('inventario', 'editar');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [itemsRes, loansRes, usersRes] = await Promise.all([
        getInventory(), getLoans({}), getUsers()
      ]);
      setItems(itemsRes.data);
      setLoans(loansRes.data);
      setUsers(usersRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.name) return toast.error('El nombre es requerido');
    try {
      await createInventoryItem(form);
      toast.success('Item creado');
      setShowCreate(false);
      resetForm();
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleUpdate = async () => {
    try {
      await updateInventoryItem(editItem.id, form);
      toast.success('Item actualizado');
      setEditItem(null);
      resetForm();
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este item?')) return;
    try {
      await deleteInventoryItem(id);
      toast.success('Item eliminado');
      fetchData();
    } catch (err) { toast.error('Error'); }
  };

  const handleLoan = async () => {
    if (!loanUser) return toast.error('Selecciona un usuario');
    try {
      await createLoan(showLoanDialog.id, loanUser, loanNotes);
      toast.success('Préstamo registrado');
      setShowLoanDialog(null);
      setLoanUser('');
      setLoanNotes('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleReturn = async (loanId) => {
    try {
      await returnLoan(loanId, '');
      toast.success('Devolución registrada');
      fetchData();
    } catch (err) { toast.error('Error'); }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({ name: item.name, category: item.category, description: item.description || '',
      quantity: item.quantity || 1, condition: item.condition || 'bueno', location: item.location || '' });
  };

  const resetForm = () => setForm({
    name: '', category: 'trajes', description: '', quantity: 1, condition: 'bueno', location: ''
  });

  const filteredItems = items.filter(item => {
    const matchSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCat = !filterCategory || item.category === filterCategory;
    return matchSearch && matchCat;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Inventario</h1>
          <p className="text-stone-500 text-sm">{items.length} items registrados</p>
        </div>
        {hasPermission('inventario', 'crear') && (
          <Button onClick={() => { setShowCreate(true); resetForm(); }} data-testid="create-inventory-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Item
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {CATEGORIES.map(cat => {
          const count = items.filter(i => i.category === cat.value).length;
          return (
            <Card key={cat.value} className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setFilterCategory(filterCategory === cat.value ? '' : cat.value)}>
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-stone-500">{cat.label}</p>
                <p className="text-xl font-bold text-stone-800">{count}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="items">
        <TabsList>
          <TabsTrigger value="items"><Package className="w-4 h-4 mr-1" />Items</TabsTrigger>
          <TabsTrigger value="loans"><HandMetal className="w-4 h-4 mr-1" />Préstamos ({loans.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="items">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                  <Input placeholder="Buscar items..." value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)} className="pl-9" data-testid="inventory-search" />
                </div>
                <Select value={filterCategory || 'all'} onValueChange={v => setFilterCategory(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[150px]"><SelectValue placeholder="Categoría" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead className="text-center">Cantidad</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Ubicación</TableHead>
                    {canEdit && <TableHead className="text-right">Acciones</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map(item => {
                    const cond = CONDITIONS.find(c => c.value === item.condition);
                    const activeLoans = loans.filter(l => l.item_id === item.id && !l.devuelto);
                    return (
                      <TableRow key={item.id} data-testid={`inventory-row-${item.id}`}>
                        <TableCell>
                          <div className="font-medium text-stone-800">{item.name}</div>
                          {item.description && <p className="text-xs text-stone-400">{item.description}</p>}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{CATEGORIES.find(c => c.value === item.category)?.label || item.category}</Badge>
                        </TableCell>
                        <TableCell className="text-center font-medium">{item.quantity}</TableCell>
                        <TableCell>
                          <Badge className={cond?.color || ''}>{cond?.label || item.condition}</Badge>
                        </TableCell>
                        <TableCell className="text-stone-500 text-sm">{item.location || '-'}</TableCell>
                        {canEdit && (
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button variant="ghost" size="icon" onClick={() => setShowLoanDialog(item)} title="Prestar"
                                data-testid={`loan-btn-${item.id}`}>
                                <HandMetal className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => openEdit(item)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="text-red-600" onClick={() => handleDelete(item.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                              {activeLoans.length > 0 && (
                                <Badge variant="secondary" className="ml-1 text-xs">{activeLoans.length} prestado(s)</Badge>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {filteredItems.length === 0 && <p className="text-center text-stone-400 py-8">No hay items</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="loans">
          <Card>
            <CardHeader><CardTitle className="text-base">Préstamos Activos</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Prestado a</TableHead>
                    <TableHead>Fecha Préstamo</TableHead>
                    <TableHead>Notas</TableHead>
                    {canEdit && <TableHead className="text-right">Acciones</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loans.map(loan => (
                    <TableRow key={loan.id} data-testid={`loan-row-${loan.id}`}>
                      <TableCell className="font-medium">{loan.item_name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-stone-400" />
                          {loan.user_name}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-stone-500">
                        {loan.fecha_prestamo ? new Date(loan.fecha_prestamo).toLocaleDateString('es-ES') : '-'}
                      </TableCell>
                      <TableCell className="text-sm text-stone-400">{loan.notas || '-'}</TableCell>
                      {canEdit && (
                        <TableCell className="text-right">
                          {!loan.devuelto && (
                            <Button size="sm" variant="outline" onClick={() => handleReturn(loan.id)}
                              data-testid={`return-loan-${loan.id}`}>
                              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Devolver
                            </Button>
                          )}
                          {loan.devuelto && <Badge className="bg-emerald-100 text-emerald-700">Devuelto</Badge>}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {loans.length === 0 && <p className="text-center text-stone-400 py-8">No hay préstamos activos</p>}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate || !!editItem} onOpenChange={() => { setShowCreate(false); setEditItem(null); resetForm(); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editItem ? 'Editar Item' : 'Nuevo Item'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="item-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Categoría</Label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Cantidad</Label>
                <Input type="number" min={0} value={form.quantity} onChange={e => setForm({...form, quantity: parseInt(e.target.value) || 0})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={form.condition} onValueChange={v => setForm({...form, condition: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONDITIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ubicación</Label>
                <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditItem(null); }}>Cancelar</Button>
            <Button onClick={editItem ? handleUpdate : handleCreate} data-testid="submit-item-btn">
              {editItem ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loan Dialog */}
      <Dialog open={!!showLoanDialog} onOpenChange={() => { setShowLoanDialog(null); setLoanUser(''); setLoanNotes(''); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Prestar: {showLoanDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Prestar a *</Label>
              <Select value={loanUser || 'none'} onValueChange={v => setLoanUser(v === 'none' ? '' : v)}>
                <SelectTrigger data-testid="loan-user"><SelectValue placeholder="Seleccionar usuario" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Seleccionar...</SelectItem>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notas</Label>
              <Input value={loanNotes} onChange={e => setLoanNotes(e.target.value)} placeholder="Notas del préstamo..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLoanDialog(null)}>Cancelar</Button>
            <Button onClick={handleLoan} data-testid="submit-loan-btn">Registrar Préstamo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
